export interface MarketRegime {
  current: 'bull' | 'bear' | 'high_volatility' | 'crisis' | 'normal';
  confidence: number;
  indicators: {
    vix: number;
    sp500_drawdown: number;
    credit_spread: number;
    correlation_spike: boolean;
  };
  historicalCorrelation: {
    normal: number;
    crisis: number;
    deltaPct: number;
  };
}

export class RegimeDetector {
  async detectCurrentRegime(): Promise<MarketRegime> {
    // Fetch latest market indicators (placeholder - would integrate with real data sources)
    const vix = await this.fetchLatestVIX();
    const sp500Drawdown = await this.fetchSP500Drawdown();
    const creditSpread = await this.fetchCreditSpread();
    
    let regime: MarketRegime['current'] = 'normal';
    let confidence = 0.8;
    
    // Crisis regime: VIX > 40, drawdown > -20%, credit spreads > 600bp
    if (vix > 40 || sp500Drawdown < -20 || creditSpread > 6.0) {
      regime = 'crisis';
      confidence = 0.95;
    }
    // Bear regime: VIX > 30, drawdown > -10%, credit spreads > 400bp
    else if (vix > 30 || sp500Drawdown < -10 || creditSpread > 4.0) {
      regime = 'bear';
      confidence = 0.85;
    }
    // High volatility regime: VIX > 20, credit spreads > 300bp
    else if (vix > 20 || creditSpread > 3.0) {
      regime = 'high_volatility';
      confidence = 0.80;
    }
    // Bull regime: VIX < 15, positive returns, credit spreads < 250bp
    else if (vix < 15 && sp500Drawdown > 0 && creditSpread < 2.5) {
      regime = 'bull';
      confidence = 0.85;
    }
    // Normal regime: default steady state
    else {
      regime = 'normal';
      confidence = 0.80;
    }
    
    const normalCorrelation = 0.25;
    const crisisCorrelation = 0.85;
    const deltaPct = ((crisisCorrelation - normalCorrelation) / normalCorrelation) * 100;
    
    return {
      current: regime,
      confidence,
      indicators: {
        vix,
        sp500_drawdown: sp500Drawdown,
        credit_spread: creditSpread,
        correlation_spike: regime === 'crisis' || regime === 'bear'
      },
      historicalCorrelation: {
        normal: normalCorrelation,
        crisis: crisisCorrelation,
        deltaPct
      }
    };
  }

  private async fetchLatestVIX(): Promise<number> {
    // Placeholder - would integrate with market data API
    // For now, return a typical bull market VIX value
    return 15.5;
  }

  private async fetchSP500Drawdown(): Promise<number> {
    // Placeholder - would calculate from recent S&P 500 data
    return -2.3;
  }

  private async fetchCreditSpread(): Promise<number> {
    // Placeholder - would fetch BAMLH0A0HYM2 (High Yield spread)
    return 2.8;
  }
}
